import './monthRangePicker.less';
import templateUrl from './monthRangePicker.html';

export default angular.module('eventix.common.directives.datePickers.monthRangePicker', [])
    .component('monthRangePicker', {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:monthRangePicker
         * @restrict element
         * @description
         * Slick date range picker. Especially useful for financial reporting.
         * Possibility to pick months, quarters or years.
         *
         * @example
         * <pre>
         * <month-range-picker start="vm.startMoment" end="vm.endMoment"></month-range-picker>
         * </pre>
         *
         * @param {moment.Moment} start Moment attribute that holds the start moment of the range
         * @param {moment.Moment} end Moment attribute that holds the end moment of the range
         */
        templateUrl,
        bindings: {
            start: '=',
            end: '='
        },
        controller: MonthRangePickerController
    })
    .name;

function MonthRangePickerController($scope, $element, $translate) {
    const $ctrl = this;

    const dragTypes = {
        MONTH: 0,
        QUARTER: 1,
        YEAR: 2,
        LEFT_HANDLE: 3,
        RIGHT_HANDLE: 4
    };

    const drag = {
        startCoordinate: 0,

        type: dragTypes.MONTH
    };

    $ctrl.monthNames = $translate.instant('common.datetime.months').split('_');
    $ctrl.quarterLabels = $translate.instant('common.datetime.quarters').split('_');
    $ctrl.years = [];
    $ctrl.selectorStyle = {width: '50%'};

    $ctrl.incrementYear = incrementYear;
    $ctrl.decrementYear = decrementYear;
    $ctrl.isNextDisabled = isNextDisabled;
    $ctrl.isPrevDisabled = isPrevDisabled;

    $ctrl.onMouseDownLeftHandleBar = onMouseDownLeftHandleBar;
    $ctrl.onMouseDownRightHandleBar = onMouseDownRightHandleBar;

    $ctrl.onMouseDownYear = onMouseDownYear;
    $ctrl.onMouseDownQuarter = onMouseDownQuarter;
    $ctrl.onMouseDownMonth = onMouseDownMonth;

    /**
     * Whenever the bindings and component DOM have been initialized
     */
    $ctrl.$onInit = function() {
        /**
         * Whenever the start date changes
         */
        $scope.$watch('$ctrl.start', calculateSelectorStyle);

        /**
         * Whenever the end date changes
         */
        $scope.$watch('$ctrl.end', calculateSelectorStyle);

        calculateYears();
    };

    /**
     * Increment the year by 1 for the year index
     * @param {number} yearIndex the year index
     */
    function incrementYear(yearIndex) {
        if ($ctrl.isNextDisabled(yearIndex)) return;

        $ctrl.years[yearIndex]++;

        calculateSelectorStyle();
    }

    /**
     * Decrement the year by 1 for the year index
     * @param {number} yearIndex the year index
     */
    function decrementYear(yearIndex) {
        if ($ctrl.isPrevDisabled(yearIndex)) return;

        $ctrl.years[yearIndex]--;

        calculateSelectorStyle();
    }

    /**
     * Next year disabled
     * @param {number} yearIndex the index of the year
     * @return {boolean} Indicates if the next year is disabled
     */
    function isNextDisabled(yearIndex) {
        return (yearIndex === 0) ? ($ctrl.years[1] === $ctrl.years[0] + 1) : false;
    }

    /**
     * Previous year disabled
     * @param {number} yearIndex the index of the year
     * @return {boolean} Indicates if the previous year is disabled
     */
    function isPrevDisabled(yearIndex) {
        return (yearIndex === 1) ? $ctrl.years[0] === $ctrl.years[1] - 1 : false;
    }

    /**
     * Whenever the left handle bar has been selected
     * @param {MouseEvent} event the event
     */
    function onMouseDownLeftHandleBar(event) {
        if (event.button !== 0) return;

        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);

        let offset = ($ctrl.start.year() === $ctrl.years[1]) ? 12 : 0;

        drag.startCoordinate = $ctrl.start.month() + offset;
        drag.type = dragTypes.LEFT_HANDLE;
    }

    /**
     * Whenever the right handle bar has been selected
     * @param {MouseEvent} event the event
     */
    function onMouseDownRightHandleBar(event) {
        if (event.button !== 0) return;

        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);

        let offset = ($ctrl.end.year() === $ctrl.years[1]) ? 12 : 0;

        drag.startCoordinate = $ctrl.end.month() + offset;
        drag.type = dragTypes.RIGHT_HANDLE;
    }

    /**
     * Whenever the mouse has been pressed within a year
     * @param {MouseEvent} event the event
     * @param {number} yearIndex the year index
     */
    function onMouseDownYear(event, yearIndex) {
        if ((' ' + event.target.className + ' ').indexOf(' timeline-year ') === -1 || event.button !== 0) return;

        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);

        drag.startCoordinate = yearIndex * 12;
        drag.type = dragTypes.YEAR;

        $ctrl.start.year($ctrl.years[yearIndex])
            .month(0)
            .date(1)
            .hour(0)
            .minute(0)
            .second(0);

        $ctrl.end.year($ctrl.years[yearIndex] + 1)
            .month(0)
            .date(1)
            .hour(0)
            .minute(0)
            .second(0)
            .subtract(1, 'seconds');

        calculateSelectorStyle();
    }

    /**
     * Whenever the mouse has been pressed within a quarter
     * @param {MouseEvent} event the event
     * @param {number} yearIndex the year index
     * @param {number} quarterIndex the quarter index
     */
    function onMouseDownQuarter(event, yearIndex, quarterIndex) {
        if (event.button !== 0) return;

        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);

        drag.startCoordinate = yearIndex * 12 + quarterIndex * 3;
        drag.type = dragTypes.QUARTER;

        $ctrl.start.year($ctrl.years[yearIndex])
            .month(quarterIndex * 3)
            .date(1)
            .hour(0)
            .minute(0)
            .second(0);

        $ctrl.end.year($ctrl.years[yearIndex])
            .month((quarterIndex + 1) * 3)
            .date(1)
            .hour(0)
            .minute(0)
            .second(0)
            .subtract(1, 'seconds');

        calculateSelectorStyle();
    }

    /**
     * Whenever the mouse has been pressed within a quarter
     * @param {MouseEvent} event the associated event
     * @param {number} yearIndex the year index
     * @param {number} monthIndex the month index
     */
    function onMouseDownMonth(event, yearIndex, monthIndex) {
        if (event.button !== 0) return;

        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);

        drag.startCoordinate = yearIndex * 12 + monthIndex;
        drag.type = dragTypes.MONTH;

        $ctrl.start.year($ctrl.years[yearIndex])
            .month(monthIndex)
            .date(1)
            .hour(0)
            .minute(0)
            .second(0);

        $ctrl.end.year($ctrl.years[yearIndex])
            .month(monthIndex + 1)
            .date(1)
            .hour(0)
            .minute(0)
            .second(0)
            .subtract(1, 'seconds');

        calculateSelectorStyle();
    }

    /**
     * Whenever the mouse move event occurs within the document
     * @param {MouseEvent} event the event associated
     */
    function onMouseMove(event) {
        if (!$element || !$element[0] || event.button !== 0) return;

        let absoluteElementCoordinate = {
            x: $element[0].getBoundingClientRect().left,
            y: $element[0].getBoundingClientRect().top
        };

        let absoluteMouseCoordinate = {
            x: event.clientX,
            y: event.clientY
        };

        let leftHandleCoordinate, rightHandleCoordinate;
        let totalCoordinates = 24;
        let totalWidth = $element[0].children[0].offsetWidth;
        let widthPerCoordinate = totalWidth / totalCoordinates;
        let startCoordinate = drag.startCoordinate;
        let endCoordinate = (absoluteMouseCoordinate.x - absoluteElementCoordinate.x) / widthPerCoordinate;

        let dragTypeModifier = 1;

        switch (drag.type) {
            case dragTypes.YEAR:
                dragTypeModifier *= 4;

                // fallthrough intentional
            case dragTypes.QUARTER:
                dragTypeModifier *= 3;

                // fallthrough intentional
            case dragTypes.MONTH:
                startCoordinate = Math.floor(startCoordinate / dragTypeModifier) * dragTypeModifier;
                endCoordinate = Math.min(24 - dragTypeModifier, Math.max(0, Math.floor(endCoordinate / dragTypeModifier) * dragTypeModifier));

                changeTimespan(startCoordinate, endCoordinate);

                break;

            case dragTypes.LEFT_HANDLE:
                rightHandleCoordinate = 23;

                $ctrl.end.add(1, 'seconds');

                if ($ctrl.years[1] === $ctrl.end.year())
                    rightHandleCoordinate = $ctrl.end.month() + 12 - 1;
                else if ($ctrl.years[0] === $ctrl.end.year())
                    rightHandleCoordinate = $ctrl.end.month() - 1;

                $ctrl.end.subtract(1, 'seconds');

                leftHandleCoordinate = Math.min(rightHandleCoordinate, Math.max(0, Math.floor(endCoordinate)));

                $ctrl.start
                    .year(leftHandleCoordinate >= 12 ? $ctrl.years[1] : $ctrl.years[0])
                    .month(leftHandleCoordinate % 12)
                    .date(1)
                    .hour(0)
                    .minute(0)
                    .second(0);

                break;

            case dragTypes.RIGHT_HANDLE:
                leftHandleCoordinate = 0;

                if ($ctrl.years[1] === $ctrl.start.year())
                    leftHandleCoordinate = $ctrl.start.month() + 12;
                else if ($ctrl.years[0] === $ctrl.start.year())
                    leftHandleCoordinate = $ctrl.start.month();
                else
                    leftHandleCoordinate = 0;

                rightHandleCoordinate = Math.min(23, Math.max(leftHandleCoordinate, Math.floor(endCoordinate)));

                $ctrl.end
                    .year(rightHandleCoordinate >= 12 ? $ctrl.years[1] : $ctrl.years[0])
                    .month(rightHandleCoordinate % 12 + 1)
                    .date(1)
                    .hour(0)
                    .minute(0)
                    .second(0)
                    .subtract(1, 'seconds');

                break;
            default:
                return;
        }

        $scope.$apply(calculateSelectorStyle);
    }

    /**
     * Change the timespan for a drag
     * @param {number} startCoordinate the coordinate of the start date in the chart
     * @param {number} endCoordinate the coordinate of the end date in the chart
     */
    function changeTimespan(startCoordinate, endCoordinate) {
        let newStartCoordinate = startCoordinate > endCoordinate ? endCoordinate : startCoordinate;
        let newEndCoordinate = startCoordinate > endCoordinate ? startCoordinate : endCoordinate;

        $ctrl.start
            .year(newStartCoordinate >= 12 ? $ctrl.years[1] : $ctrl.years[0])
            .month(newStartCoordinate % 12)
            .date(1)
            .hour(0)
            .minute(0)
            .second(0);

        $ctrl.end
            .year(newEndCoordinate >= 12 ? $ctrl.years[1] : $ctrl.years[0])
            .month(newEndCoordinate % 12 + 1)
            .date(1)
            .hour(0)
            .minute(0)
            .second(0)
            .subtract(1, 'seconds');
    }

    /**
     * Whenever the mouse up event occurs within the document
     * @param {MouseEvent} event the event associated
     */
    function onMouseUp(event) {
        document.removeEventListener('mouseup', onMouseUp);
        document.removeEventListener('mousemove', onMouseMove);
    }

    /**
     * Calculate the style of the selector
     */
    function calculateSelectorStyle() {
        if (!$ctrl.start || !$ctrl.end) return;

        let widthPercentagePerYear = 50;
        let yearMS = 365.254 * 24 * 3600 * 1000;
        let deltaYears = $ctrl.years[1] - $ctrl.years[0];

        let leftCoordinate = Math.round(($ctrl.start - moment([$ctrl.years[0], 0, 1])) / yearMS * 12) / 12;
        let rightCoordinate = Math.round(($ctrl.end - moment([$ctrl.years[0], 0, 1])) / yearMS * 12) / 12;
        let deltaCoordinates;

        if (leftCoordinate < 1) {
            // Start from left year in chart
            leftCoordinate = Math.max(0, leftCoordinate);

            if (rightCoordinate > 1 && $ctrl.end.year() < $ctrl.years[1] && $ctrl.end.year() !== $ctrl.start.year())
                deltaCoordinates = 1 - leftCoordinate;
            else if (rightCoordinate > 1)
                deltaCoordinates = rightCoordinate - leftCoordinate - (deltaYears - 1);
            else
                deltaCoordinates = rightCoordinate - leftCoordinate;

            $ctrl.selectorStyle = Object.assign($ctrl.selectorStyle, {
                left: widthPercentagePerYear * leftCoordinate + '%',
                width: widthPercentagePerYear * Math.min(2 - leftCoordinate, deltaCoordinates) + '%'
            });
        } else {
            // Start from right year in chart. Calculate starting coordinate relative to the second year
            leftCoordinate = Math.round(($ctrl.start - moment([$ctrl.years[1], 0, 1])) / yearMS * 12) / 12;
            rightCoordinate = Math.round(($ctrl.end - moment([$ctrl.years[1], 0, 1])) / yearMS * 12) / 12;

            if (leftCoordinate < 0)
                leftCoordinate = 0;

            if (rightCoordinate > 1)
                rightCoordinate = 1;

            deltaCoordinates = rightCoordinate - leftCoordinate;

            $ctrl.selectorStyle = Object.assign($ctrl.selectorStyle, {
                left: widthPercentagePerYear * (leftCoordinate + 1) + '%',
                width: widthPercentagePerYear * Math.min(1 - leftCoordinate, deltaCoordinates) + '%'
            });
        }
    }

    /**
     * Calculates the years array based on the start and end date
     */
    function calculateYears() {
        if (_.isNil($ctrl.start)) $ctrl.start = moment().utc().subtract(1, 'year').startOf('month');
        if (_.isNil($ctrl.end)) $ctrl.end = moment().utc().endOf('month');

        let startYear = $ctrl.start.year();
        let endYear = $ctrl.end.year();

        $ctrl.years = [
            (startYear === endYear) ? startYear - 1 : startYear,
            endYear
        ];
    }
}